package kz.dentalcrm.app.screens.positions.edit

import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.error_message
import dentalcrm.composeapp.generated.resources.snackbar_position_deleted
import dentalcrm.composeapp.generated.resources.snackbar_position_updated
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch
import kz.dentalcrm.app.core.base.BaseStateViewModel
import kz.dentalcrm.app.core.utils.Result
import kz.dentalcrm.app.domain.PositionsRepository
import kz.dentalcrm.app.screens.positions.edit.mvi.EditPositionViewEvent
import kz.dentalcrm.app.screens.positions.edit.mvi.EditPositionViewSideEffect
import kz.dentalcrm.app.screens.positions.edit.mvi.EditPositionViewState
import kz.dentalcrm.app.theme.SnackbarController
import kz.dentalcrm.app.theme.SnackbarEvent
import org.jetbrains.compose.resources.getString
import org.koin.core.component.inject

class EditPositionViewModel :
    BaseStateViewModel<EditPositionViewEvent, EditPositionViewState, EditPositionViewSideEffect>() {

    private val positionsRepository: PositionsRepository by inject()

    override fun setInitialState() = EditPositionViewState()

    override fun handleEvents(event: EditPositionViewEvent) {
        when (event) {
            is EditPositionViewEvent.EditPosition -> {
                editPosition(event.position.id, event.name, event.description)
            }
            is EditPositionViewEvent.DeletePosition -> {
                deletePosition(event.position.id)
            }
            EditPositionViewEvent.Reset -> {
                setState { copy(screenState = EditPositionViewState.ScreenState.Idle) }
            }
        }
    }

    private fun editPosition(id: Int, name: String, description: String) {
        setState { copy(screenState = EditPositionViewState.ScreenState.Editing) }
        launch(Dispatchers.Default) {
            when (val result = positionsRepository.editPosition(id, name, description)) {
                is Result.Success -> {
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.snackbar_position_updated), SnackbarEvent.SnackbarType.Green))
                    setState { copy(screenState = EditPositionViewState.ScreenState.Edited) }
                }
                is Result.Error -> {
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.error_message), SnackbarEvent.SnackbarType.Red))
                    setState {
                        copy(
                            screenState = EditPositionViewState.ScreenState.Error(
                                error = result.message ?: "Error editing position"
                            )
                        )
                    }
                }
                else -> {
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.error_message), SnackbarEvent.SnackbarType.Red))
                    setState {
                        copy(
                            screenState = EditPositionViewState.ScreenState.Error(
                                error = "Unknown error"
                            )
                        )
                    }
                }
            }
        }
    }

    private fun deletePosition(id: Int) {
        setState { copy(screenState = EditPositionViewState.ScreenState.Deleting) }
        launch(Dispatchers.Default) {
            when (val result = positionsRepository.deletePosition(id)) {
                is Result.Success -> {
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.snackbar_position_deleted), SnackbarEvent.SnackbarType.Green))
                    setState { copy(screenState = EditPositionViewState.ScreenState.Deleted) }
                }
                is Result.Error -> {
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.error_message), SnackbarEvent.SnackbarType.Red))
                    setState {
                        copy(
                            screenState = EditPositionViewState.ScreenState.Error(
                                error = result.message ?: "Error deleting position"
                            )
                        )
                    }
                }
                else -> {
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.error_message), SnackbarEvent.SnackbarType.Red))
                    setState {
                        copy(
                            screenState = EditPositionViewState.ScreenState.Error(
                                error = "Unknown error"
                            )
                        )
                    }
                }
            }
        }
    }
}
